import { API_URL, HASH_API_TOKEN } from "config";
import { useQuery } from "@tanstack/react-query";
import { managedFetch } from "utils/FetchManager";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";

const buildUrl = (baseEndpoint, queryParams = {}) => {
  const url = new URL(`${API_URL}${baseEndpoint}`);

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.append(key, String(value));
    }
  });

  return url.toString();
};

export default function useFetch(endpoint, options = {}, enabled = true) {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { params, ...fetchOptions } = options;
  const url = buildUrl(endpoint, params);
  const queryKey = [url.replace(API_URL, "")];
  const isSharePage = /viewer\//.test(location.pathname);
  const apiToken = isSharePage ? HASH_API_TOKEN : currentUser?.accessToken;

  const { data, error, isLoading, isRefetching, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!apiToken) {
        throw new Error("No authentication token available");
      }

      const res = await managedFetch(
        url,
        {
          ...fetchOptions,
          headers: {
            Authorization: `Bearer ${apiToken}`,
            "Content-Type": "application/json",
          },
        },
        isSharePage ? null : currentUser
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch ${endpoint}`);
      }
      return res.json();
    },
    enabled,
    staleTime: 60000,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      console.error(`Error calling GET ${endpoint}: ${error}`);
      options.onError && options.onError(error);
    }
  }, [error, endpoint, options]);

  return { data, error, isLoading, isRefetching, refetch };
}
